import PropTypes from 'prop-types';
import { useEffect, useRef, cloneElement } from 'react';
// @mui
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
// components
import Logo from 'src/components/logo';
import AuthHelpLink from 'src/sections/auth/AuthHelpLink';

// ----------------------------------------------------------------------

// Lohri Bonfire Canvas Component
function LohriCanvas() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            let animationFrameId;
            let flames = [];
            let sparks = [];
            let embers = [];
            let textParticles = [];

            // Set canvas size
            const resizeCanvas = () => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
            };
            resizeCanvas();
            window.addEventListener('resize', resizeCanvas);

            // Helper: Create Flame Object
            const createFlame = (x, y) => ({
                baseX: x,
                baseY: y,
                x: x + (Math.random() - 0.5) * 40,
                y,
                targetY: y - Math.random() * 150 - 100,
                radius: Math.random() * 20 + 10,
                speed: Math.random() * 2 + 1,
                alpha: 1,
                decay: Math.random() * 0.01 + 0.005,
                wobble: Math.random() * Math.PI * 2,
                wobbleSpeed: Math.random() * 0.1 + 0.05,
            });

            // Helper: Update Flame
            const updateFlame = (flame) => {
                flame.wobble += flame.wobbleSpeed;
                flame.x = flame.baseX + Math.sin(flame.wobble) * 30;
                flame.y -= flame.speed;
                flame.alpha -= flame.decay;
                flame.radius *= 0.99;
            };

            // Helper: Draw Flame
            const drawFlame = (flame) => {
                const gradient = ctx.createRadialGradient(flame.x, flame.y, 0, flame.x, flame.y, flame.radius);
                gradient.addColorStop(0, `rgba(255, 255, 200, ${flame.alpha})`);
                gradient.addColorStop(0.3, `rgba(255, 180, 0, ${flame.alpha * 0.8})`);
                gradient.addColorStop(0.6, `rgba(255, 100, 0, ${flame.alpha * 0.5})`);
                gradient.addColorStop(1, `rgba(200, 0, 0, 0)`);

                ctx.save();
                ctx.globalCompositeOperation = 'lighter';
                ctx.beginPath();
                ctx.arc(flame.x, flame.y, flame.radius, 0, Math.PI * 2);
                ctx.fillStyle = gradient;
                ctx.fill();
                ctx.restore();
            };

            // Helper: Create Spark Object
            const createSpark = (x, y) => ({
                x,
                y,
                radius: Math.random() * 3 + 1,
                velocity: {
                    x: (Math.random() - 0.5) * 6,
                    y: -Math.random() * 8 - 4,
                },
                gravity: 0.15,
                friction: 0.98,
                alpha: 1,
                decay: Math.random() * 0.02 + 0.01,
                color: Math.random() > 0.5 ? '#FFD700' : '#FF6B00',
            });

            // Helper: Update Spark
            const updateSpark = (spark) => {
                spark.velocity.x *= spark.friction;
                spark.velocity.y += spark.gravity;

                spark.x += spark.velocity.x;
                spark.y += spark.velocity.y;

                spark.alpha -= spark.decay;
            };

            // Helper: Draw Spark
            const drawSpark = (spark) => {
                ctx.save();
                ctx.globalAlpha = spark.alpha;
                ctx.shadowBlur = 10;
                ctx.shadowColor = spark.color;
                ctx.beginPath();
                ctx.arc(spark.x, spark.y, spark.radius, 0, Math.PI * 2);
                ctx.fillStyle = spark.color;
                ctx.fill();
                ctx.restore();
            };

            // Helper: Create Ember Object
            const createEmber = () => ({
                x: Math.random() * canvas.width,
                y: canvas.height + 10,
                targetY: Math.random() * canvas.height * 0.3,
                radius: Math.random() * 4 + 2,
                speed: Math.random() * 3 + 2,
                alpha: Math.random() * 0.5 + 0.5,
                pulseSpeed: Math.random() * 0.05 + 0.02,
                pulse: 0,
            });

            // Helper: Update Ember
            const updateEmber = (ember) => {
                ember.y -= ember.speed;
                ember.pulse += ember.pulseSpeed;
                ember.speed *= 0.99;

                // Return true if it should be removed
                return ember.y <= ember.targetY;
            };

            // Helper: Draw Ember
            const drawEmber = (ember) => {
                const pulseAlpha = ember.alpha + Math.sin(ember.pulse) * 0.3;
                ctx.save();
                ctx.globalAlpha = pulseAlpha;
                ctx.shadowBlur = 15;
                ctx.shadowColor = '#FF4500';
                ctx.beginPath();
                ctx.arc(ember.x, ember.y, ember.radius, 0, Math.PI * 2);
                ctx.fillStyle = '#FF6347';
                ctx.fill();
                ctx.restore();
            };

            // Helper: Create Text Particle
            const createTextParticle = (x, y, char, index) => ({
                x,
                y,
                char,
                delay: index * 12,
                time: 0,
                alpha: 0,
                scale: 0,
                targetScale: 1,
                color: ['#FFD700', '#FF6B00', '#FF4500', '#FFA500'][index % 4],
                wobble: 0,
                wobbleSpeed: 0.05,
            });

            // Helper: Update Text Particle
            const updateTextParticle = (tp) => {
                tp.time += 1;
                if (tp.time > tp.delay) {
                    tp.alpha = Math.min(1, tp.alpha + 0.15);
                    tp.scale = Math.min(tp.targetScale, tp.scale + 0.15);
                    tp.wobble += tp.wobbleSpeed;
                }
            };

            // Helper: Draw Text Particle
            const drawTextParticle = (tp) => {
                if (tp.time < tp.delay) return;

                ctx.save();
                ctx.globalAlpha = tp.alpha;
                ctx.font = `bold ${50 * tp.scale}px Arial`;
                ctx.fillStyle = tp.color;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.shadowBlur = 20;
                ctx.shadowColor = tp.color;

                const offsetY = Math.sin(tp.wobble) * 5;
                ctx.fillText(tp.char, tp.x, tp.y + offsetY);
                ctx.restore();
            };

            // Create bonfire flames
            const createFlames = (x, y, count = 5) => {
                for (let i = 0; i < count; i += 1) {
                    flames.push(createFlame(x, y));
                }
            };

            // Create sparks
            const createSparks = (x, y, count = 3) => {
                for (let i = 0; i < count; i += 1) {
                    sparks.push(createSpark(x, y));
                }
            };

            // Create HAPPY LOHRI text
            const createLohriText = () => {
                const text = 'HAPPY LOHRI';

                // On desktop, position text in the right-side empty space (approx 70% width)
                // On mobile, keep it centered
                const isDesktop = canvas.width >= 900;
                const centerX = isDesktop ? canvas.width * 0.70 : canvas.width / 2;
                const centerY = canvas.height * 0.20; // Lower slightly to 20%

                const charSpacing = 45;
                const startX = centerX - (text.length * charSpacing) / 2;

                textParticles = [];
                for (let i = 0; i < text.length; i += 1) {
                    const char = text[i];
                    const charX = startX + i * charSpacing;
                    textParticles.push(createTextParticle(charX, centerY, char, i));
                }
            };

            // Bonfire positions (responsive)
            const getBonfirePositions = () => {
                const isMobile = canvas.width < 768;
                if (isMobile) {
                    return [{ x: canvas.width / 2, y: canvas.height * 0.7 }];
                }
                return [
                    { x: canvas.width * 0.2, y: canvas.height * 0.75 },
                    { x: canvas.width * 0.8, y: canvas.height * 0.75 },
                ];
            };

            // Initialize text
            createLohriText();

            // Animation loop
            const animate = () => {
                ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                const bonfirePositions = getBonfirePositions();

                // Create flames continuously
                bonfirePositions.forEach((pos) => {
                    if (Math.random() < 0.3) {
                        createFlames(pos.x, pos.y, 3);
                    }
                    if (Math.random() < 0.1) {
                        createSparks(pos.x, pos.y, 2);
                    }
                });

                // Random embers
                if (Math.random() < 0.05) {
                    embers.push(createEmber());
                }

                // Update and draw flames
                flames = flames.filter((flame) => {
                    drawFlame(flame);
                    updateFlame(flame);
                    return flame.alpha > 0;
                });

                // Update and draw sparks
                sparks = sparks.filter((spark) => {
                    drawSpark(spark);
                    updateSpark(spark);
                    return spark.alpha > 0;
                });

                // Update and draw embers
                embers = embers.filter((ember) => {
                    drawEmber(ember);
                    const shouldRemove = updateEmber(ember);
                    return !shouldRemove;
                });

                // Update and draw text
                textParticles.forEach((tp) => {
                    updateTextParticle(tp);
                    drawTextParticle(tp);
                });

                animationFrameId = requestAnimationFrame(animate);
            };

            animate();

            // Recreate text on resize
            window.addEventListener('resize', createLohriText);

            // Cleanup
            return () => {
                window.removeEventListener('resize', resizeCanvas);
                window.removeEventListener('resize', createLohriText);
                cancelAnimationFrame(animationFrameId);
            };
        }
        return undefined;
    }, []);

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 2,
            }}
        />
    );
}

export default function LohriTheme({ children }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box sx={{
            width: '100vw', height: '100vh', position: 'relative',
            display: 'flex', alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-start' },
            px: { xs: 2, md: 12 },
            backgroundImage: 'url(/assets/background/festival_lohri_bg.png)',
            backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
            overflow: 'hidden',
            '&:before': {
                content: '""', position: 'absolute', inset: 0,
                background: `linear-gradient(to bottom, ${alpha(theme.palette.grey[900], 0.6)}, ${alpha('#1a0000', 0.8)})`,
                zIndex: 0,
            },
        }}>
            <LohriCanvas />
            <Card sx={{
                p: { xs: 3, sm: 4, md: 5 }, width: 1, maxWidth: 460, zIndex: 3, position: 'relative',
                bgcolor: isDark ? 'rgba(15,5,0,0.96)' : 'rgba(255,255,255,0.97)',
                backdropFilter: 'blur(24px)', borderRadius: 3,
                border: `1px solid ${alpha('#FF5722', 0.4)}`,
                boxShadow: [
                    `0 4px 24px ${alpha('#000', 0.45)}`,
                    `0 16px 64px ${alpha('#FF5722', 0.2)}`,
                ].join(', '),
                '&:before': {
                    content: '""', position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px',
                    background: 'linear-gradient(90deg, transparent, #FF5722, #FFD700, #FF5722, transparent)',
                    borderRadius: '0 0 4px 4px',
                    animation: 'lohriGlow 3s ease-in-out infinite',
                },
                '@keyframes lohriGlow': { '0%, 100%': { opacity: 0.6 }, '50%': { opacity: 1 } },
            }}>
                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box component="span" sx={{
                        display: 'inline-block', px: 2, py: 0.5, borderRadius: 10,
                        background: 'linear-gradient(90deg, #FF5722, #FFD700, #FF5722)',
                        backgroundSize: '200% 100%',
                        animation: 'badgeShimmer 2.5s ease infinite',
                        '@keyframes badgeShimmer': { '0%': { backgroundPosition: '100% 0' }, '100%': { backgroundPosition: '-100% 0' } },
                        fontSize: '0.72rem', fontWeight: 800, letterSpacing: 2, color: '#3A0E00', textTransform: 'uppercase',
                        boxShadow: `0 2px 12px ${alpha('#FF5722', 0.5)}`,
                    }}>🔥 &nbsp; Happy Lohri &nbsp; 🔥</Box>
                </Box>
                <Stack alignItems="center" sx={{ mb: 2 }}><Logo /></Stack>
                {cloneElement(children, { hideHelpLink: true })}
            </Card>
            <AuthHelpLink sx={{
                position: 'fixed', top: '50%', right: 0,
                transform: 'translateY(-50%) rotate(-90deg)', transformOrigin: 'right center',
                zIndex: 9999, bgcolor: alpha('#FF5722', 0.92), p: 1, px: 2.5,
                borderRadius: '8px 8px 0 0', boxShadow: `0 4px 20px ${alpha('#000', 0.3)}`,
                pointerEvents: 'auto', cursor: 'pointer',
                '& .MuiLink-root': { color: '#3A0E00', fontWeight: 700, fontSize: '0.8rem', pointerEvents: 'auto', cursor: 'pointer' },
            }} />
        </Box>
    );
}

LohriTheme.propTypes = {
    children: PropTypes.node,
};
